import React, { useState } from "react";
import { Modal, message, Badge, Tooltip } from "antd";
import {
  CarOutlined,
  UserOutlined,
  PhoneOutlined,
  CopyOutlined,
  FilePdfOutlined,
  EditOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  WhatsAppOutlined,
  CompassOutlined,
  HomeOutlined
} from "@ant-design/icons";
import apiClient from "../utils/apiClient";

const TripTransportOperations = ({ booking, lead, services = [], bookingId, onRefreshBooking }) => {
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [fullTourModalOpen, setFullTourModalOpen] = useState(false);
  const [briefModalOpen, setBriefModalOpen] = useState(false);
  const [loadingBrief, setLoadingBrief] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [savingDay, setSavingDay] = useState(false);

  const [briefContent, setBriefContent] = useState("");

  const [selectedDayNumber, setSelectedDayNumber] = useState(1);
  const [dayForm, setDayForm] = useState({
    pickupLocation: "",
    dropLocation: "",
    pickupTime: "",
    reportingTime: "",
    vehicleType: "",
    vehicleNumber: "",
    driverName: "",
    driverPhone: "",
    supplierName: "",
    notes: "",
    status: "ASSIGNED",
  });

  const [fullTourForm, setFullTourForm] = useState({
    supplierName: "",
    driverName: "",
    driverPhone: "",
    vehicleType: "",
    vehicleNumber: "",
    reportingTime: "08:00 AM",
  });

  const days = booking?.transportPlan?.days || [];

  // Determine overall transport status
  const isFullyAssigned = days.length > 0 && days.every((d) => d.status === "ASSIGNED" || (d.driverName || d.driver?.name));
  const isPartiallyAssigned = days.some((d) => d.status === "ASSIGNED" || (d.driverName || d.driver?.name));

  const customerName = booking?.customer?.name || booking?.customer?.customerName || lead?.customerName || "Guest";
  const customerPhone = booking?.customer?.phone || lead?.phone || "N/A";
  const destination = lead?.destinationSnapshot || booking?.destination?.city || "N/A";

  const departureDate = booking?.departureDate
    ? new Date(booking.departureDate).toLocaleDateString("en-IN")
    : lead?.travelDate
    ? new Date(lead.travelDate).toLocaleDateString("en-IN")
    : "TBD";

  const returnDate = booking?.returnDate
    ? new Date(booking.returnDate).toLocaleDateString("en-IN")
    : lead?.returnDate
    ? new Date(lead.returnDate).toLocaleDateString("en-IN")
    : "TBD";

  // Handle Edit Day click
  const handleOpenEditDay = (day) => {
    setSelectedDayNumber(day.dayNumber);
    const driverStr = typeof day.driver === "object" ? day.driver?.name : (day.driver || day.driverName);
    const driverPhoneStr = typeof day.driver === "object" ? day.driver?.phone : day.driverPhone;
    const supplierStr = typeof day.supplier === "object" ? day.supplier?.name : (day.supplier || day.supplierName);

    setDayForm({
      pickupLocation: day.pickupLocation || "",
      dropLocation: day.dropLocation || "",
      pickupTime: day.pickupTime || "",
      reportingTime: day.reportingTime || "",
      vehicleType: day.vehicleType || "",
      vehicleNumber: day.vehicleNumber || "",
      driverName: driverStr || "",
      driverPhone: driverPhoneStr || "",
      supplierName: supplierStr || "",
      notes: day.notes || "",
      status: day.status || "ASSIGNED",
    });
    setAssignModalOpen(true);
  };

  // Save Day Transport Assignment
  const handleSaveDay = async () => {
    if (!bookingId) {
      message.error("Booking not found");
      return;
    }
    setSavingDay(true);
    try {
      await apiClient.put(`/bookings/${bookingId}/transport-day/${selectedDayNumber}`, dayForm);
      message.success(`Day ${selectedDayNumber} transport details updated`);
      setAssignModalOpen(false);
      if (onRefreshBooking) onRefreshBooking();
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || "Failed to update day transport");
    } finally {
      setSavingDay(false);
    }
  };

  // Save Full Tour Assignment
  const handleSaveFullTour = async () => {
    if (!bookingId || days.length === 0) {
      message.error("No transport days available");
      return;
    }
    setSavingDay(true);
    try {
      for (const day of days) {
        await apiClient.put(`/bookings/${bookingId}/transport-day/${day.dayNumber}`, {
          supplierName: fullTourForm.supplierName,
          driverName: fullTourForm.driverName,
          driverPhone: fullTourForm.driverPhone,
          vehicleType: fullTourForm.vehicleType || day.vehicleType,
          vehicleNumber: fullTourForm.vehicleNumber || day.vehicleNumber,
          reportingTime: fullTourForm.reportingTime,
          status: "ASSIGNED",
        });
      }
      message.success("Full tour transport vendor & driver assigned successfully!");
      setFullTourModalOpen(false);
      if (onRefreshBooking) onRefreshBooking();
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || "Failed to assign full tour transport");
    } finally {
      setSavingDay(false);
    }
  };

  // Generate Brief & Open Modal
  const handleGenerateBrief = async () => {
    if (!bookingId) return;
    setLoadingBrief(true);
    try {
      const res = await apiClient.post(`/bookings/${bookingId}/transport/brief`);
      const briefText = res.data?.data?.brief || "";
      setBriefContent(briefText);
      setBriefModalOpen(true);
      message.success("Transport brief generated");
      if (onRefreshBooking) onRefreshBooking();
    } catch (err) {
      console.error(err);
      message.error(err.response?.data?.message || "Failed to generate brief");
    } finally {
      setLoadingBrief(false);
    }
  };

  // Copy Brief to Clipboard
  const handleCopyWhatsAppBrief = async () => {
    let textToCopy = briefContent;
    if (!textToCopy && bookingId) {
      try {
        const res = await apiClient.post(`/bookings/${bookingId}/transport/brief`);
        textToCopy = res.data?.data?.brief || "";
        setBriefContent(textToCopy);
      } catch (err) {
        console.error(err);
      }
    }

    if (!textToCopy) {
      // Fallback local format
      const hotelText = services.length > 0
        ? services.map((s, i) => `${i + 1}. ${s.hotelName || s.title} (${s.location?.city || "City"})`).join("\n")
        : "N/A";
      
      const daysText = days.map((d) => 
        `Day ${d.dayNumber}: ${d.pickupLocation || "TBD"} ➔ ${d.dropLocation || "TBD"} | Vehicle: ${d.vehicleType || "-"} | Driver: ${d.driverName || d.driver?.name || "Pending"} (${d.driverPhone || d.driver?.phone || "-"})`
      ).join("\n");

      textToCopy = `🚘 *TRANSPORT BRIEF - ${booking?.bookingReference || "TRIP"}*
👤 *Guest:* ${customerName} (${customerPhone})
📍 *Destination:* ${destination}
📅 *Dates:* ${departureDate} to ${returnDate}

🏨 *Hotels:*
${hotelText}

🚘 *Itinerary:*
${daysText}`;
    }

    try {
      await navigator.clipboard.writeText(textToCopy);
      message.success("WhatsApp Brief copied to clipboard!");
    } catch (err) {
      console.error(err);
      message.info("Copied transport details!");
    }
  };

  // Download PDF
  const handleDownloadPDF = async () => {
    if (!bookingId) return;
    setDownloadingPdf(true);
    try {
      const response = await apiClient.get(`/bookings/${bookingId}/transport/pdf`, {
        responseType: "blob",
      });
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Transport_Brief_${booking?.bookingReference || "Booking"}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      message.success("Vendor Transport PDF downloaded successfully");
    } catch (err) {
      console.error(err);
      message.error("Failed to download Transport PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!bookingId) {
    return (
      <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-8 text-center">
        <CarOutlined className="text-4xl text-gray-400 mb-3" />
        <h3 className="text-lg font-bold text-gray-800">No Booking Created Yet</h3>
        <p className="text-sm text-gray-500 mt-1">
          Convert an accepted quotation to a trip booking to enable transport management & driver assignments.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* HEADER BANNER */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-3">
              <span className="p-2.5 rounded-xl bg-blue-50 text-blue-600">
                <CarOutlined className="text-xl" />
              </span>
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="text-xl font-bold text-gray-900">Transport & Operations Dashboard</h2>
                  {isFullyAssigned ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                      <CheckCircleOutlined /> Fully Assigned
                    </span>
                  ) : isPartiallyAssigned ? (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800">
                      <ClockCircleOutlined /> Partially Assigned
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-rose-100 text-rose-800">
                      <ExclamationCircleOutlined /> Pending Assignment
                    </span>
                  )}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Ref: <strong className="text-gray-700">{booking?.bookingReference}</strong> • Guest:{" "}
                  <strong className="text-gray-700">{customerName}</strong> ({customerPhone})
                </p>
              </div>
            </div>
          </div>

          {/* QUICK ACTIONS */}
          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => setFullTourModalOpen(true)}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-gray-300 bg-white text-gray-700 text-xs font-semibold hover:bg-gray-50 transition"
            >
              <CarOutlined /> Assign Full Tour
            </button>

            <button
              onClick={handleGenerateBrief}
              disabled={loadingBrief}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl border border-blue-200 bg-blue-50 text-blue-700 text-xs font-semibold hover:bg-blue-100 transition"
            >
              <CopyOutlined /> {loadingBrief ? "Generating..." : "Generate Brief"}
            </button>

            <button
              onClick={handleCopyWhatsAppBrief}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 transition"
            >
              <WhatsAppOutlined /> Copy WhatsApp Template
            </button>

            <button
              onClick={handleDownloadPDF}
              disabled={downloadingPdf}
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-xl bg-gray-900 text-white text-xs font-semibold hover:bg-gray-800 transition"
            >
              <FilePdfOutlined /> {downloadingPdf ? "Downloading..." : "Vendor PDF"}
            </button>
          </div>
        </div>

        {/* DETAILS GRID */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-5 border-t border-gray-100">
          <div className="bg-gray-50 p-3.5 rounded-xl">
            <div className="text-xs text-gray-500 font-medium">Guest Mobile</div>
            <div className="text-sm font-bold text-gray-900 mt-0.5 flex items-center gap-1.5">
              <PhoneOutlined className="text-emerald-600" />
              <a href={`tel:${customerPhone}`} className="hover:underline">
                {customerPhone}
              </a>
            </div>
          </div>

          <div className="bg-gray-50 p-3.5 rounded-xl">
            <div className="text-xs text-gray-500 font-medium">Destination & Duration</div>
            <div className="text-sm font-bold text-gray-900 mt-0.5 flex items-center gap-1.5">
              <CompassOutlined className="text-blue-600" />
              {destination} ({booking?.duration || lead?.duration || 1} Days)
            </div>
          </div>

          <div className="bg-gray-50 p-3.5 rounded-xl">
            <div className="text-xs text-gray-500 font-medium">Travel Dates</div>
            <div className="text-sm font-bold text-gray-900 mt-0.5">
              {departureDate} ➔ {returnDate}
            </div>
          </div>

          <div className="bg-gray-50 p-3.5 rounded-xl">
            <div className="text-xs text-gray-500 font-medium">Operations Exec</div>
            <div className="text-sm font-bold text-gray-900 mt-0.5">
              {booking?.teams?.operations?.name || "Assigned Team"}
            </div>
          </div>
        </div>
      </div>

      {/* HOTEL ACCOMMODATION SUMMARY */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <HomeOutlined className="text-lg text-blue-600" />
            <h3 className="text-base font-bold text-gray-900">Hotel Pickup & Stay Details</h3>
          </div>
          <span className="text-xs font-semibold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full">
            {services.length} Hotel Bookings
          </span>
        </div>

        {services.length === 0 ? (
          <p className="text-xs text-gray-500 italic">No hotel services attached to this booking yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold">
                  <th className="py-2.5 px-3">Hotel Name</th>
                  <th className="py-2.5 px-3">City / Location</th>
                  <th className="py-2.5 px-3">Check-In / Out</th>
                  <th className="py-2.5 px-3">Room Category</th>
                  <th className="py-2.5 px-3">Meal Plan</th>
                  <th className="py-2.5 px-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {services.map((h, i) => (
                  <tr key={h._id || i} className="hover:bg-gray-50/50">
                    <td className="py-2.5 px-3 font-semibold text-gray-900">{h.hotelName || h.title}</td>
                    <td className="py-2.5 px-3 text-gray-600">{h.location?.city || "-"}</td>
                    <td className="py-2.5 px-3 text-gray-700">
                      {h.checkIn ? new Date(h.checkIn).toLocaleDateString("en-IN") : "-"} ➔{" "}
                      {h.checkOut ? new Date(h.checkOut).toLocaleDateString("en-IN") : "-"}
                    </td>
                    <td className="py-2.5 px-3 text-gray-600">{h.roomCategory || h.roomType || "Standard"}</td>
                    <td className="py-2.5 px-3 text-gray-600">{h.mealPlan || "CP"}</td>
                    <td className="py-2.5 px-3">
                      <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-50 text-emerald-700">
                        {h.status || "Confirmed"}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* DAY-WISE TRANSPORT ITINERARY & ASSIGNMENTS TABLE */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-base font-bold text-gray-900">Day-Wise Transport Assignments</h3>
            <p className="text-xs text-gray-500">Manage vehicle numbers, drivers, suppliers, and pickup timings</p>
          </div>
        </div>

        {days.length === 0 ? (
          <div className="p-6 text-center text-sm text-gray-500 border border-dashed border-gray-200 rounded-xl">
            No transport days created yet. Please update the quotation itinerary.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-gray-600 font-semibold uppercase tracking-wider">
                  <th className="py-3 px-3">Day</th>
                  <th className="py-3 px-3">Route (Pickup ➔ Drop)</th>
                  <th className="py-3 px-3">Vehicle & Reg No.</th>
                  <th className="py-3 px-3">Driver & Mobile</th>
                  <th className="py-3 px-3">Supplier / Vendor</th>
                  <th className="py-3 px-3">Reporting / Pickup Time</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {days.map((day) => {
                  const driverStr = typeof day.driver === "object" ? day.driver?.name : (day.driver || day.driverName);
                  const driverPhoneStr = typeof day.driver === "object" ? day.driver?.phone : day.driverPhone;
                  const supplierStr = typeof day.supplier === "object" ? day.supplier?.name : (day.supplier || day.supplierName);
                  const isAssigned = day.status === "ASSIGNED" || Boolean(driverStr);

                  return (
                    <tr key={day.dayNumber || day._id} className="hover:bg-gray-50/80 transition">
                      <td className="py-3.5 px-3 font-bold text-gray-900">
                        <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-gray-100 text-gray-800 font-bold">
                          D{day.dayNumber}
                        </span>
                      </td>

                      <td className="py-3.5 px-3 max-w-[200px]">
                        <div className="font-semibold text-gray-900">
                          {day.pickupLocation || "Pickup TBD"} ➔ {day.dropLocation || "Drop TBD"}
                        </div>
                        {day.notes && <div className="text-[11px] text-gray-400 truncate mt-0.5">{day.notes}</div>}
                      </td>

                      <td className="py-3.5 px-3">
                        <div className="font-semibold text-gray-800">{day.vehicleType || "Vehicle TBD"}</div>
                        {day.vehicleNumber ? (
                          <div className="text-[11px] font-mono text-blue-600 font-semibold bg-blue-50 px-1.5 py-0.5 rounded inline-block mt-0.5">
                            {day.vehicleNumber}
                          </div>
                        ) : (
                          <div className="text-[11px] text-amber-600">No Reg No.</div>
                        )}
                      </td>

                      <td className="py-3.5 px-3">
                        {driverStr ? (
                          <div>
                            <div className="font-semibold text-gray-900 flex items-center gap-1">
                              <UserOutlined className="text-gray-400" /> {driverStr}
                            </div>
                            {driverPhoneStr && (
                              <div className="text-[11px] text-gray-500 flex items-center gap-1 mt-0.5">
                                <PhoneOutlined className="text-emerald-600" />
                                <a href={`tel:${driverPhoneStr}`} className="hover:underline">
                                  {driverPhoneStr}
                                </a>
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-rose-500 font-medium italic">Unassigned</span>
                        )}
                      </td>

                      <td className="py-3.5 px-3">
                        <div className="font-medium text-gray-700">{supplierStr || "Self / In-house"}</div>
                      </td>

                      <td className="py-3.5 px-3">
                        <div className="text-gray-800 font-medium">{day.reportingTime || day.pickupTime || "08:00 AM"}</div>
                      </td>

                      <td className="py-3.5 px-3">
                        {isAssigned ? (
                          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                            Assigned
                          </span>
                        ) : (
                          <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                            Pending
                          </span>
                        )}
                      </td>

                      <td className="py-3.5 px-3 text-right">
                        <button
                          onClick={() => handleOpenEditDay(day)}
                          className="px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-700 font-semibold text-xs hover:bg-gray-50 transition"
                        >
                          <EditOutlined /> Edit
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* MODAL: ASSIGN / EDIT SINGLE DAY */}
      <Modal
        title={`Assign Transport - Day ${selectedDayNumber}`}
        open={assignModalOpen}
        onCancel={() => setAssignModalOpen(false)}
        onOk={handleSaveDay}
        confirmLoading={savingDay}
        okText="Save Assignment"
        okButtonProps={{ className: "bg-black text-white" }}
      >
        <div className="space-y-4 my-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Pickup Location</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.pickupLocation}
                onChange={(e) => setDayForm({ ...dayForm, pickupLocation: e.target.value })}
                placeholder="e.g. Airport / Hotel"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Drop Location</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.dropLocation}
                onChange={(e) => setDayForm({ ...dayForm, dropLocation: e.target.value })}
                placeholder="e.g. Hotel / Sightseeing"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Vehicle Type</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.vehicleType}
                onChange={(e) => setDayForm({ ...dayForm, vehicleType: e.target.value })}
                placeholder="e.g. Innova Crysta / Dzire"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Vehicle Reg Number</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.vehicleNumber}
                onChange={(e) => setDayForm({ ...dayForm, vehicleNumber: e.target.value })}
                placeholder="e.g. KA-01-AB-1234"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Supplier / Vendor</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.supplierName}
                onChange={(e) => setDayForm({ ...dayForm, supplierName: e.target.value })}
                placeholder="e.g. Royal Travels"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Reporting / Pickup Time</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.reportingTime}
                onChange={(e) => setDayForm({ ...dayForm, reportingTime: e.target.value })}
                placeholder="e.g. 08:30 AM"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Driver Name</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.driverName}
                onChange={(e) => setDayForm({ ...dayForm, driverName: e.target.value })}
                placeholder="Driver full name"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Driver Phone</label>
              <input
                type="text"
                className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
                value={dayForm.driverPhone}
                onChange={(e) => setDayForm({ ...dayForm, driverPhone: e.target.value })}
                placeholder="Driver 10-digit mobile"
              />
            </div>
          </div>

          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Notes / Instructions</label>
            <textarea
              className="w-full text-xs p-2.5 border border-gray-300 rounded-lg outline-none focus:border-black"
              rows={2}
              value={dayForm.notes}
              onChange={(e) => setDayForm({ ...dayForm, notes: e.target.value })}
              placeholder="e.g. Guest prefers English speaking driver, flight pickup with nameboard"
            />
          </div>
        </div>
      </Modal>

      {/* MODAL: ASSIGN FULL TOUR */}
      <Modal
        title="Assign Full Tour Vendor & Driver"
        open={fullTourModalOpen}
        onCancel={() => setFullTourModalOpen(false)}
        onOk={handleSaveFullTour}
        confirmLoading={savingDay}
        okText="Apply to All Days"
        okButtonProps={{ className: "bg-black text-white" }}
      >
        <p className="text-xs text-gray-500 mb-4">
          This will update supplier, driver, vehicle reg, and reporting time across all {days.length} days of this trip.
        </p>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-gray-600 block mb-1">Supplier / Vendor Name</label>
            <input
              type="text"
              className="w-full text-xs p-2 border border-gray-300 rounded-lg"
              value={fullTourForm.supplierName}
              onChange={(e) => setFullTourForm({ ...fullTourForm, supplierName: e.target.value })}
              placeholder="e.g. Mountain Cabs Pvt Ltd"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Driver Name</label>
              <input
                type="text"
                className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                value={fullTourForm.driverName}
                onChange={(e) => setFullTourForm({ ...fullTourForm, driverName: e.target.value })}
                placeholder="Driver Name"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Driver Phone</label>
              <input
                type="text"
                className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                value={fullTourForm.driverPhone}
                onChange={(e) => setFullTourForm({ ...fullTourForm, driverPhone: e.target.value })}
                placeholder="Mobile number"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Vehicle Type</label>
              <input
                type="text"
                className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                value={fullTourForm.vehicleType}
                onChange={(e) => setFullTourForm({ ...fullTourForm, vehicleType: e.target.value })}
                placeholder="e.g. Innova Crysta"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-gray-600 block mb-1">Vehicle Reg Number</label>
              <input
                type="text"
                className="w-full text-xs p-2 border border-gray-300 rounded-lg"
                value={fullTourForm.vehicleNumber}
                onChange={(e) => setFullTourForm({ ...fullTourForm, vehicleNumber: e.target.value })}
                placeholder="e.g. KA-01-EF-5678"
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* MODAL: VIEW BRIEF & WHATSAPP TEMPLATE */}
      <Modal
        title="Transport Brief & WhatsApp Template"
        open={briefModalOpen}
        onCancel={() => setBriefModalOpen(false)}
        footer={[
          <button
            key="copy"
            onClick={handleCopyWhatsAppBrief}
            className="px-4 py-2 bg-emerald-600 text-white rounded-xl text-xs font-semibold hover:bg-emerald-700 transition mr-2"
          >
            <WhatsAppOutlined /> Copy to WhatsApp
          </button>,
          <button
            key="close"
            onClick={() => setBriefModalOpen(false)}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-xl text-xs font-semibold hover:bg-gray-300 transition"
          >
            Close
          </button>,
        ]}
      >
        <div className="bg-gray-50 border border-gray-200 p-4 rounded-xl max-h-96 overflow-y-auto font-mono text-xs whitespace-pre-wrap text-gray-800">
          {briefContent || "No brief generated yet. Click 'Generate Brief'."}
        </div>
      </Modal>
    </div>
  );
};

export default TripTransportOperations;
